// Detalle de un correo del asistente (panel lateral): datos, texto, datos extraídos, adjuntos,
// correos reenviados (hijos) y reclasificación manual.
import { useCallback, useEffect, useRef, useState } from 'react';
import { X, Mail, Paperclip, Loader2, CornerDownRight, ArrowLeft, RotateCcw, FolderOpen } from 'lucide-react';
import { api } from '../../api/client';
import { Can } from '../../lib/rbac';
import { StatusBadge } from '../../components/shared/StatusBadge';
import {
  loadMeta,
  typeLabel,
  statusTone,
  fmtDateTime,
  type AssistantMeta,
  type EmailDetail,
  type EmailRow,
  type MailboxWithStats,
  folderLabel,
  fmtValue,
  dataKeyLabel,
  emailStateWords,
  caseKindLabel,
  caseStatusLabel,
  caseStatusTone,
  type ActionRow,
} from './shared';
import { ActionsMini } from './ActionsMini';

type Detail = EmailDetail & {
  children: EmailRow[];
  links: Array<{ entity_type: string; entity_id: string }>;
  /** Tramo 4 (opcionales: un servidor viejo no los manda). */
  case?: { id: number; kind: string; status: string; title: string } | null;
  actions?: ActionRow[];
};

/** Resultado de "Reintentar acción" (POST /emails/:id/retry-handler). */
interface RetryResult {
  status: string;
  handler_summary: string | null;
  case_id?: number | null;
}

/** Cómo se clasificó el correo, en palabras. */
function classifiedByLabel(v: string | null): string {
  if (!v) return '—';
  if (v === 'manual') return 'A mano';
  if (v.startsWith('rule:')) return 'Por regla fija';
  if (v === 'ai') return 'Por inteligencia artificial';
  if (v.startsWith('ai:')) return 'La inteligencia artificial no pudo decidir';
  return v;
}

function fmtConfidence(v: string | number | null): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${Math.round(n <= 1 ? n * 100 : n)} %`;
}

function fmtSize(bytes: number): string {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-xs py-1">
      <span className="w-32 shrink-0 text-[var(--color-text-muted)]">{label}</span>
      <span className="min-w-0 break-words text-[var(--color-text)]">{children}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">{title}</div>
      {children}
    </div>
  );
}

export function EmailDetailDrawer({
  emailId,
  sellerName,
  onClose,
  onChanged,
  onOpenCase,
}: {
  emailId: number;
  sellerName: (id: number) => string;
  onClose: () => void;
  onChanged: () => void;
  /** Si se pasa, el caso enlazado muestra "Ver caso" (cambia a la pestaña Casos). */
  onOpenCase?: (caseId: number) => void;
}) {
  // Pila de correos abiertos: al abrir un hijo se apila, "Volver" regresa al anterior.
  const [stack, setStack] = useState<number[]>([emailId]);
  const currentId = stack[stack.length - 1];
  const currentIdRef = useRef(currentId);
  currentIdRef.current = currentId;

  const [meta, setMeta] = useState<AssistantMeta | null>(null);
  const [metaFailed, setMetaFailed] = useState(false);
  const [metaTry, setMetaTry] = useState(0);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mailboxBySeller, setMailboxBySeller] = useState<Record<number, string>>({});
  const [newType, setNewType] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  const showToast = (t: typeof toast) => {
    setToast(t);
    if (t) setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    setStack([emailId]);
  }, [emailId]);

  // Etiquetas de tipos y estados antes de dibujar (nunca mostrar la clave cruda).
  useEffect(() => {
    let alive = true;
    setMetaFailed(false);
    void loadMeta().then((m) => {
      if (!alive) return;
      if (m) setMeta(m);
      else setMetaFailed(true);
    });
    return () => {
      alive = false;
    };
  }, [metaTry]);

  // Dirección de la casilla de cada vendedor (para el dato "Casilla").
  useEffect(() => {
    let alive = true;
    api
      .get<MailboxWithStats[]>('/assistant/mailboxes')
      .then((res) => {
        if (!alive || !res.success || !res.data) return;
        const map: Record<number, string> = {};
        for (const m of res.data) map[m.seller_id] = m.address;
        setMailboxBySeller(map);
      })
      .catch(() => {
        /* sin casilla no bloquea el detalle */
      });
    return () => {
      alive = false;
    };
  }, []);

  // Solo vale la respuesta del último pedido: si se abre otro correo antes de que llegue, se descarta.
  const reqSeq = useRef(0);
  const load = useCallback(async (id: number) => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<Detail>(`/assistant/emails/${id}`);
      if (seq !== reqSeq.current) return;
      if (res.success && res.data) {
        setDetail(res.data);
        setNewType(res.data.type ?? '');
      } else {
        setDetail(null);
        setError(res.error || 'No se pudo abrir el correo.');
      }
    } catch {
      if (seq !== reqSeq.current) return;
      setDetail(null);
      setError('No se pudo abrir el correo.');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(currentId);
  }, [currentId, load]);

  const reclassify = async () => {
    if (!detail || !newType || newType === detail.type) return;
    setSaving(true);
    try {
      const res = await api.patch<Detail>(`/assistant/emails/${detail.id}/type`, { type: newType });
      if (res.success && res.data) {
        setDetail(res.data);
        setNewType(res.data.type ?? '');
        showToast({ kind: 'ok', msg: 'Tipo actualizado' });
        onChanged();
      } else {
        showToast({ kind: 'err', msg: res.error || 'No se pudo cambiar el tipo.' });
      }
    } catch {
      showToast({ kind: 'err', msg: 'No se pudo cambiar el tipo.' });
    } finally {
      setSaving(false);
    }
  };

  // "Reintentar acción": vuelve a correr el manejador del correo y muestra el resultado en línea.
  // El resultado (y la espera) se guardan por id de correo: al cambiar de correo en la pila no se mezclan.
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [retryMsg, setRetryMsg] = useState<{ id: number; kind: 'ok' | 'err'; msg: string } | null>(null);
  const retryHandler = async () => {
    if (!detail) return;
    const id = detail.id;
    setRetryingId(id);
    setRetryMsg(null);
    try {
      const res = await api.post<RetryResult>(`/assistant/emails/${id}/retry-handler`, {});
      if (res.success && res.data) {
        onChanged();
        const st = emailStateWords(res.data.status);
        const summary = res.data.handler_summary ? ` — ${res.data.handler_summary}` : '';
        setRetryMsg({ id, kind: 'ok', msg: `Resultado: ${st}${summary}` });
        if (currentIdRef.current === id) void load(id);
      } else {
        setRetryMsg({ id, kind: 'err', msg: res.error || 'No se pudo reintentar la acción.' });
      }
    } catch {
      setRetryMsg({ id, kind: 'err', msg: 'No se pudo reintentar la acción.' });
    } finally {
      setRetryingId((cur) => (cur === id ? null : cur));
    }
  };
  const retrying = retryingId === currentId;
  const retryMsgHere = retryMsg && retryMsg.id === currentId ? retryMsg : null;

  // Descarga del adjunto pasando por el cliente de la API (renueva la sesión si venció).
  const [downloading, setDownloading] = useState<string | null>(null);
  const downloadAttachment = async (id: number, n: number, filename: string) => {
    setDownloading(`${id}:${n}`);
    try {
      const blob = await api.getBlob(`/assistant/emails/${id}/attachments/${n}`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `adjunto-${n}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      showToast({ kind: 'err', msg: 'No se pudo descargar el adjunto.' });
    } finally {
      setDownloading(null);
    }
  };

  const payloadEntries = detail?.payload ? Object.entries(detail.payload) : [];

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-black/50" onClick={onClose}>
      <div
        className="w-full max-w-2xl h-full bg-[var(--color-bg)] border-l border-[var(--color-border)] shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera */}
        <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {stack.length > 1 && (
              <button
                onClick={() => setStack((s) => s.slice(0, -1))}
                title="Volver al correo anterior"
                aria-label="Volver al correo anterior"
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <Mail size={18} className="text-[var(--color-primary)] shrink-0" />
            <div className="text-sm font-bold text-[var(--color-text)] truncate" title={detail?.subject ?? ''}>
              {detail ? detail.subject || '(sin asunto)' : 'Correo'}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {toast && (
            <div
              className={`rounded-lg p-3 text-sm ${
                toast.kind === 'ok'
                  ? 'bg-[var(--color-success-bg,rgba(34,197,94,0.1))] text-[var(--color-success)]'
                  : 'bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]'
              }`}
            >
              {toast.msg}
            </div>
          )}

          {metaFailed && !meta ? (
            <div className="rounded-lg p-3 text-sm bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)] flex items-center justify-between gap-2">
              <span>No se pudieron cargar las listas</span>
              <button
                type="button"
                onClick={() => setMetaTry((n) => n + 1)}
                className="px-3 py-1 text-xs rounded border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)]"
              >
                Reintentar
              </button>
            </div>
          ) : loading || !meta ? (
            <div className="flex items-center justify-center py-12 text-[var(--color-text-secondary)]">
              <Loader2 size={18} className="animate-spin mr-2" /> Cargando…
            </div>
          ) : error || !detail ? (
            <div className="rounded-lg p-3 text-sm bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]">
              {error || 'No se pudo abrir el correo.'}
            </div>
          ) : (
            <>
              <Section title="Datos">
                <Field label="Empresa">{sellerName(detail.seller_id)}</Field>
                <Field label="Casilla">{mailboxBySeller[detail.seller_id] ?? '—'}</Field>
                <Field label="Carpeta">{folderLabel(detail.folder)}</Field>
                <Field label="Remitente">{detail.from_addr || '—'}</Field>
                <Field label="Destinatario">{detail.to_addr || '—'}</Field>
                <Field label="Fecha">{fmtDateTime(detail.received_at)}</Field>
                <Field label="Tipo">{typeLabel(detail.type)}</Field>
                <Field label="Cómo se clasificó">{classifiedByLabel(detail.classified_by)}</Field>
                <Field label="Confianza">{fmtConfidence(detail.confidence)}</Field>
                <Field label="Estado">
                  <StatusBadge status={statusTone(detail.status)} label={emailStateWords(detail.status)} size="sm" />
                </Field>
                {detail.parent_email_id !== null && (
                  <Field label="Reenviado dentro de">
                    <button
                      onClick={() => setStack((s) => [...s, detail.parent_email_id as number])}
                      className="text-[var(--color-primary)] hover:underline"
                    >
                      Ver el correo que lo contiene
                    </button>
                  </Field>
                )}
              </Section>

              <Section title="Qué hizo el asistente">
                <Field label="Resumen">{detail.handler_summary || '—'}</Field>
                <Field label="Caso">
                  {detail.case ? (
                    <span className="inline-flex flex-wrap items-center gap-2">
                      <span>{detail.case.title || caseKindLabel(detail.case.kind)}</span>
                      <StatusBadge
                        status={caseStatusTone(detail.case.status)}
                        label={caseStatusLabel(detail.case.status)}
                        size="sm"
                      />
                      {onOpenCase && (
                        <button
                          type="button"
                          onClick={() => onOpenCase(detail.case!.id)}
                          className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
                        >
                          <FolderOpen size={12} /> Ver caso
                        </button>
                      )}
                    </span>
                  ) : (
                    'Sin caso'
                  )}
                </Field>
                <div className="pt-1">
                  <ActionsMini actions={detail.actions} />
                </div>
                <Can cap="assistant.actions">
                  <div className="flex flex-wrap items-center gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => void retryHandler()}
                      disabled={retrying}
                      className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)] disabled:opacity-40"
                    >
                      {retrying ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                      Reintentar acción
                    </button>
                    {retryMsgHere && (
                      <span
                        className={`text-xs ${
                          retryMsgHere.kind === 'ok' ? 'text-[var(--color-success)]' : 'text-[var(--color-error)]'
                        }`}
                      >
                        {retryMsgHere.msg}
                      </span>
                    )}
                  </div>
                </Can>
              </Section>

              <Can cap="assistant.manage">
                <Section title="Reclasificar">
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      value={newType}
                      onChange={(e) => setNewType(e.target.value)}
                      className="px-2 py-1 text-xs bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded text-[var(--color-text)]"
                    >
                      {!detail.type && <option value="">Elegí un tipo…</option>}
                      {/* "Enviado por el asistente" no se elige a mano (solo figura si ya lo es) */}
                      {meta.types.filter((t) => t.key !== 'own_sent' || t.key === detail.type).map((t) => (
                        <option key={t.key} value={t.key}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={reclassify}
                      disabled={saving || !newType || newType === detail.type}
                      className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-40"
                    >
                      {saving && <Loader2 size={12} className="animate-spin" />}
                      Cambiar tipo
                    </button>
                  </div>
                </Section>
              </Can>

              <Section title="Texto del correo">
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-3 text-xs text-[var(--color-text)] whitespace-pre-wrap break-words max-h-[45vh] overflow-y-auto">
                  {detail.text_clean || '(sin texto)'}
                </div>
              </Section>

              {payloadEntries.length > 0 && (
                <Section title="Datos extraídos">
                  {payloadEntries.map(([k, v]) => (
                    <Field key={k} label={dataKeyLabel(k)}>
                      {fmtValue(v)}
                    </Field>
                  ))}
                </Section>
              )}

              {detail.attachments.length > 0 && (
                <Section title="Adjuntos">
                  <ul className="space-y-1">
                    {detail.attachments.map((a, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs">
                        <Paperclip size={12} className="text-[var(--color-text-muted)] shrink-0" />
                        {a.path ? (
                          <button
                            type="button"
                            onClick={() => void downloadAttachment(detail.id, i + 1, a.filename)}
                            disabled={downloading === `${detail.id}:${i + 1}`}
                            className="inline-flex items-center gap-1 text-left text-[var(--color-primary)] hover:underline break-all disabled:opacity-60"
                          >
                            {downloading === `${detail.id}:${i + 1}` && (
                              <Loader2 size={12} className="animate-spin shrink-0" />
                            )}
                            {a.filename}
                          </button>
                        ) : (
                          <span className="text-[var(--color-text-secondary)] break-all" title="No se guardó una copia">
                            {a.filename} (no disponible)
                          </span>
                        )}
                        <span className="text-[var(--color-text-muted)]">{fmtSize(a.size)}</span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {detail.children.length > 0 && (
                <Section title="Correos reenviados dentro de este">
                  <ul className="space-y-1">
                    {detail.children.map((c) => (
                      <li key={c.id}>
                        <button
                          onClick={() => setStack((s) => [...s, c.id])}
                          className="w-full flex items-center gap-2 text-left text-xs px-2 py-1.5 rounded hover:bg-[var(--color-surface-hover)]"
                        >
                          <CornerDownRight size={12} className="text-[var(--color-text-muted)] shrink-0" />
                          <span className="min-w-0 flex-1 truncate text-[var(--color-text)]">
                            {c.subject || '(sin asunto)'}
                          </span>
                          <span className="shrink-0 text-[var(--color-text-muted)]">{typeLabel(c.type)}</span>
                          <StatusBadge status={statusTone(c.status)} label={emailStateWords(c.status)} size="sm" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
